import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Bitcoin, Landmark, Zap, Bot, Plus, TrendingUp, PieChart as PieIcon, Lock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const TABS = [
  { id: "cartera", label: "Cartera", icon: PieIcon, alwaysOn: true },
  { id: "cripto", label: "Cripto", icon: Bitcoin, flag: "crypto_module" },
  { id: "snaptrade", label: "SnapTrade", icon: Landmark, flag: "snaptrade" },
  { id: "trading", label: "Trading", icon: Zap, flag: "trading_real_matriz", altFlag: "trading_real_users" },
  { id: "robot", label: "Robot", icon: Bot, flag: "robot_matriz", altFlag: "robot_users" },
];

export default function Inversiones() {
  const { flags, isMatriz } = useFeatureFlags();
  const [tab, setTab] = useState("cartera");
  const [assets, setAssets] = useState([]);
  const [networth, setNetworth] = useState(null);

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/networth").then((r) => setNetworth(r.data)).catch(() => {});
  }, []);

  const isFlagOn = (flag, altFlag) => {
    const a = flag && flags[flag]?.enabled;
    const b = altFlag && flags[altFlag]?.enabled;
    return a || b;
  };

  const availableTabs = TABS.filter((t) => t.alwaysOn || isFlagOn(t.flag, t.altFlag));
  // Cartera de inversión: solo activos de tipo inversión + cripto + SnapTrade
  const investmentAssets = useMemo(() =>
    (assets || []).filter((a) => ["Cuenta de inversión", "Fondo", "Acciones", "Cripto", "Otros"].includes(a.type) && (a.balance || 0) > 0),
    [assets]
  );
  const investmentTotal = investmentAssets.reduce((s, a) => s + (a.balance || 0), 0);
  const cryptoTotal = networth?.crypto_total || 0;
  const totalInvestment = investmentTotal + cryptoTotal;

  return (
    <div className="space-y-6" data-testid="inversiones-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-3">
            <Sparkles size={28} strokeWidth={2.5} /> Inversiones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Cartera consolidada del núcleo patrimonial · integraciones opcionales · trading (según permisos).</p>
        </div>
        <Link to="/activos"><Button variant="outline" data-testid="add-manual-investment"><Plus size={14} className="mr-1" /> Añadir inversión manual</Button></Link>
      </div>

      {/* KPI cabecera */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="kpi-total-inversion">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total inversiones</div>
          <Money value={totalInvestment} className="block mt-1 text-3xl font-heading font-extrabold tabular" />
          <div className="text-[11px] text-muted-foreground mt-1">Núcleo patrimonial · siempre disponible</div>
        </Card>
        <Card className="p-5" data-testid="kpi-manual">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Manuales</div>
          <Money value={investmentTotal} className="block mt-1 text-2xl font-semibold tabular" />
          <div className="text-[11px] text-muted-foreground mt-1">{investmentAssets.length} posiciones registradas</div>
        </Card>
        <Card className="p-5" data-testid="kpi-crypto">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Cripto sincronizada</div>
          <Money value={cryptoTotal} className="block mt-1 text-2xl font-semibold tabular" />
          <div className="text-[11px] text-muted-foreground mt-1">
            {isFlagOn("crypto_module") ? "Módulo activo" : <span className="text-amber-600">Módulo desactivado</span>}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border" data-testid="inversiones-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const enabled = t.alwaysOn || isFlagOn(t.flag, t.altFlag);
          return (
            <button
              key={t.id}
              onClick={() => enabled && setTab(t.id)}
              disabled={!enabled}
              data-testid={`inv-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? "border-primary text-foreground"
                  : enabled
                  ? "border-transparent text-muted-foreground hover:text-foreground"
                  : "border-transparent text-muted-foreground/40 cursor-not-allowed"
              }`}
              title={enabled ? "" : "Funcionalidad no activada"}
            >
              <Icon size={14} /> {t.label}
              {!enabled && <Lock size={11} className="opacity-60" />}
            </button>
          );
        })}
      </div>

      {tab === "cartera" && <CarteraTab items={investmentAssets} cryptoTotal={cryptoTotal} />}
      {tab === "cripto" && <ModuleTab active={isFlagOn("crypto_module")} title="Módulo Cripto"
        description="Fiscalidad FIFO, señales técnicas, sincronización con Binance/Coinbase."
        ctaLabel="Ir al módulo Cripto" ctaHref="/cripto" />}
      {tab === "snaptrade" && <ModuleTab active={isFlagOn("snaptrade")} title="SnapTrade"
        description="Sincronización de tus cuentas de broker (posiciones, balances) con lectura únicamente."
        ctaLabel="Configurar SnapTrade" ctaHref="#" />}
      {tab === "trading" && <ModuleTab active={isFlagOn("trading_real_matriz", "trading_real_users")} title="Trading Real"
        description="Ejecución de órdenes reales en exchanges. Con límites configurables y modo dry-run."
        ctaLabel={isMatriz ? "Ir a Trading" : "Solicitar activación"} ctaHref={isMatriz ? "/matriz" : "#"} />}
      {tab === "robot" && <ModuleTab active={isFlagOn("robot_matriz", "robot_users")} title="Robot Trading"
        description="Automatización de estrategias con backtesting y paper trading antes del live."
        ctaLabel={isMatriz ? "Ir al Robot" : "Solicitar activación"} ctaHref={isMatriz ? "/cripto" : "#"} />}
    </div>
  );
}

function CarteraTab({ items, cryptoTotal }) {
  const total = items.reduce((s, a) => s + (a.balance || 0), 0) + cryptoTotal;
  const rows = [...items];
  if (cryptoTotal > 0) rows.push({ id: "__crypto", name: "Cripto sincronizada", type: "Cripto", balance: cryptoTotal, provider: "Binance/Coinbase", is_derived: true });
  if (rows.length === 0) {
    return (
      <Card className="p-10 text-center space-y-3" data-testid="cartera-empty">
        <TrendingUp className="mx-auto text-muted-foreground" size={32} />
        <div className="font-heading text-lg font-bold">Aún no tienes inversiones registradas</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">Añade fondos, acciones, ETFs, cripto u otros activos manualmente desde el módulo Activos, o activa una integración para sincronizarlos automáticamente.</p>
        <div className="flex justify-center gap-2 pt-2">
          <Link to="/activos"><Button data-testid="cartera-empty-cta-manual"><Plus size={14} className="mr-1" /> Añadir manual</Button></Link>
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="cartera-list">
      <Card className="p-4 flex justify-between items-center">
        <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total cartera</span>
        <Money value={total} className="tabular font-heading text-2xl font-extrabold" />
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((a) => (
          <Card key={a.id} className="p-4" data-testid={`inv-asset-${a.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-heading font-bold">{a.name}</div>
                <div className="text-xs text-muted-foreground">{a.type}{a.provider ? ` · ${a.provider}` : ""}</div>
              </div>
              <div className="text-right">
                <Money value={a.balance} className="tabular font-heading text-xl font-bold" />
                {a.is_derived && <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">Sincronizado</div>}
              </div>
            </div>
            <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-primary rounded-full" style={{ width: `${Math.min(100, (a.balance / total) * 100)}%` }} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ModuleTab({ active, title, description, ctaLabel, ctaHref }) {
  return (
    <Card className={`p-8 text-center space-y-4 ${active ? "" : "opacity-90 border-dashed"}`}>
      <div className="flex justify-center">
        {active ? (
          <Badge className="bg-emerald-500 text-white">Activo</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            <Lock size={11} className="mr-1" /> No activado
          </Badge>
        )}
      </div>
      <h2 className="font-heading text-2xl font-extrabold">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-lg mx-auto">{description}</p>
      {active ? (
        <div className="pt-2">
          <Link to={ctaHref}><Button data-testid={`module-cta-${title.toLowerCase().replace(/\s+/g, "-")}`}>{ctaLabel}</Button></Link>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">Esta funcionalidad se activa desde el Panel MATRIZ. Contacta con soporte si necesitas habilitarla.</p>
      )}
    </Card>
  );
}
